import React, { useState, useCallback } from "react";
import { ReactController } from "../react_controller";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReadOnlyPrimerGrid } from "../../components/grids";
import { RoleAssignmentDialog } from "../../components/grids/RoleAssignmentDialog";
import { toastManager } from "../../components/DownloadTray/ToastManager";
import PrimerStyleGuard from "../../components/PrimerStyleGuard";

const GRID_OPEN_MULTI_SELECT_EVENT = "grid:open-multi-select";

const queryClient = new QueryClient();

function EventAssignmentsGrid({
  gridProps,
  assignRoleUrl,
  removeRoleUrl,
  updateReportAccessUrl,
  roleOptions,
  orgRoleName,
}) {
  const [roleDialog, setRoleDialog] = useState({
    isOpen: false,
    eventId: null,
    eventTitle: "",
    currentRoleId: null,
  });

  const getCsrfToken = () =>
    document.querySelector('meta[name="csrf-token"]')?.content;

  const handleSaveRole = useCallback(
    async (eventId, roleId) => {
      const csrfToken = getCsrfToken();

      const response = await fetch(assignRoleUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ target_event_id: eventId, role_id: roleId }),
      });

      if (!response.ok) {
        toastManager.show({
          message: "Failed to assign role",
          variant: "error",
          autoCloseMs: 3000,
        });
        // GR-1590 D1: throw so the caller's await rejects. Previously this
        // returned, which let handleRoleDialogSave (and other callers)
        // continue on to reloadGrid + onClose as if the save succeeded —
        // dialog dismissed silently on 403/422 while the server had
        // rejected the change.
        throw new Error("Failed to assign role");
      }

      toastManager.show({
        message: roleId ? "Role assigned successfully" : "Role set to org default",
        variant: "success",
        icon: "check",
        autoCloseMs: 3000,
      });
    },
    [assignRoleUrl]
  );

  const handleSaveReportAccess = useCallback(
    async (eventId, reportIdsJson) => {
      const csrfToken = getCsrfToken();

      const response = await fetch(updateReportAccessUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          target_event_id: eventId,
          report_ids: reportIdsJson,
        }),
      });

      if (!response.ok) {
        toastManager.show({
          message: "Failed to update report access",
          variant: "error",
          autoCloseMs: 3000,
        });
        // GR-1590 D1: same pattern as handleSaveRole — throw rather than
        // return so callers don't silently treat the failure as success.
        throw new Error("Failed to update report access");
      }

      toastManager.show({
        message: "Report access updated",
        variant: "success",
        icon: "check",
        autoCloseMs: 3000,
      });
    },
    [updateReportAccessUrl]
  );

  const handleSaveCell = useCallback(
    async (row, column, value) => {
      if (column.key === "report_access") {
        return handleSaveReportAccess(row.id, value);
      }
    },
    [handleSaveReportAccess]
  );

  const handleMenuAction = useCallback((button) => {
    if (button.key === "assign_role" && button.data) {
      setRoleDialog({
        isOpen: true,
        eventId: button.data.eventId,
        eventTitle: button.data.eventTitle,
        currentRoleId: button.data.currentRoleId || null,
      });
      return;
    }

    if (button.key === "assign_reports" && button.data?.eventId) {
      window.dispatchEvent(
        new CustomEvent(GRID_OPEN_MULTI_SELECT_EVENT, {
          detail: { rowId: button.data.eventId, columnKey: "report_access" },
        })
      );
    }
  }, []);

  const reloadGrid = useCallback(() => {
    queryClient.invalidateQueries();
  }, []);

  const handleRoleDialogSave = useCallback(
    async (roleId) => {
      await handleSaveRole(roleDialog.eventId, roleId);
      reloadGrid();
    },
    [roleDialog.eventId, handleSaveRole, reloadGrid]
  );

  return (
    <>
      <ReadOnlyPrimerGrid
        {...gridProps}
        onSaveCell={handleSaveCell}
        onMenuAction={handleMenuAction}
      />
      <RoleAssignmentDialog
        isOpen={roleDialog.isOpen}
        onClose={() => setRoleDialog((d) => ({ ...d, isOpen: false }))}
        onSave={handleRoleDialogSave}
        eventTitle={roleDialog.eventTitle}
        currentRoleId={roleDialog.currentRoleId}
        orgRoleName={orgRoleName}
        roles={roleOptions || []}
      />
    </>
  );
}

export class EventAssignmentsGridController extends ReactController {
  connect() {
    const {
      assignRoleUrl,
      removeRoleUrl,
      updateReportAccessUrl,
      roleOptions,
      orgRoleName,
      ...gridProps
    } = this.propsValue;

    // ReadOnlyPrimerGrid wraps itself in GregPrimerTheme via
    // PaginatedApplicationPrimerGrid, but RoleAssignmentDialog renders as a
    // sibling — its sx-prop styles sit outside any StyleSheetManager.
    // PrimerStyleGuard closes that gap (same Turbo head-merge mechanism
    // as GR-1598). No BaseStyles here because the grid already nests one.
    this.root.render(
      <PrimerStyleGuard>
        <QueryClientProvider client={queryClient}>
          <EventAssignmentsGrid
            gridProps={gridProps}
            assignRoleUrl={assignRoleUrl}
            removeRoleUrl={removeRoleUrl}
            updateReportAccessUrl={updateReportAccessUrl}
            roleOptions={roleOptions}
            orgRoleName={orgRoleName}
          />
        </QueryClientProvider>
      </PrimerStyleGuard>
    );
  }
}
